<template>
  <div class="min-h-screen w-full">
    <!-- DELETE MODAL -->
    <div
      v-if="showPostDeleteModal"
      id="showModal"
      class="fixed z-70 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >

        <!-- Modal panel, show/hide based on modal state. -->
        <div
          :class="[
            showPostDeleteModalContents
              ? 'translate-y-0'
              : 'translate-y-full opacity-0',
          ]"
          class="bg-white transform delay-100 w-full sm:max-w-3xl sm:w-full inline-block rounded-lg p-12 sm:mt-8 sm:mb-20 sm:p-16 text-left shadow transition-all align-bottom sm:align-middle overflow-visible relative"
        >
          <h2 class="w-full mb-4 text-center"><span>Delete Post</span></h2>

          <p class="mb-4 text-md text-center">
            Do you want to delete this post forever?
          </p>

          <button
            :disabled="thinkingDelete"
            type="submit"
            :class="thinkingDelete ? 'btn-primary-locked' : ''"
            class="btn btn-primary m-auto mt-10 relative"
            @click="confirmDeletePost(showPostDeleteModal)"
          >
            <div
              class="absolute inset-0 w-full flex justify-center items-center z-20"
            >
              <lottie
                v-if="thinkingDelete"
                :options="loadingAnimationOptions"
              />
            </div>
            <span :class="[thinkingDelete ? 'invisible' : '']">Confirm</span>
          </button>

          <button
            type="button"
            class="absolute right-8 top-6 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
            @click="showPostDeleteModal = false"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div
      :class="isDarkMode ? 'bg-truegray-700 text-white' : 'bg-white text-black'"
      class="post-intro py-12 pt-36 lg:pt-12 lg:h-100 w-full relative z-10 flex flex-col justify-end pb-12 overflow-y-visible"
    >
      <div
        :class="isDarkMode ? 'border-white' : 'border-truegray-300'"
        class="px-4 lg:px-6 lg:ml-16 lg:border-l-8"
      >
        <h1 class=""><span class="capitalize font-primary">Drafts</span></h1>
        <h4 class="m-0">All of your unpublished posts</h4>
      </div>
    </div>

    <div
      id="posts"
      :class="[loaded ? 'bg-truegray-100' : 'bg-truegray-500']"
      class="w-full px-8 py-16 lg:p-16"
    >
      <div v-show="loaded" class="w-full">
        <button
          v-if="user"
          class="btn btn-black flex items-center space-x-2"
          @click="newPost"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <span>New Post</span>
        </button>
      </div>

      <div
        class="transition-all duration-300 transform"
        :class="[
          loaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
        ]"
      >
        <div
          v-if="loaded"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4"
        >
          <PostsCard
            v-for="(post, i) in posts"
            :id="post.id"
            :key="i"
            :ref="'posts-' + i"
            :post="post"
            :parent="component_id"
          />
        </div>
      </div>

      <div
        v-if="!loaded"
        class="h-52 flex flex-col justify-center items-center"
      >
        <lottie :options="loadingAnimationOptions" />
        <span class="text-white text-center">Loading posts</span>
      </div>

      <p
        v-if="loaded && posts.length < 1 && filterPostsBy.category != 3"
        class="text-md"
      >
        We could not find any posts...
      </p>
      <div v-if="loaded && posts.length < 1 && filterPostsBy.category === 3">
        <p class="text-md">
          We could not find any current events. To try to view previous events,
          <span
            class="cursor-pointer font-primary text-primary underline font-bold"
            @click="getOldEventPosts()"
            >click here</span
          >
          or use the filters above.
        </p>
      </div>

      <!-- PAGINATION -->
      <div
        v-if="pagination.previouspage || pagination.nextpage"
        class="flex justify-between items-center h-10"
      >
        <span
          :disabled="!pagination.previouspage"
          :class="[
            !pagination.previouspage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="getPosts(pagination.previouspage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 17l-5-5m0 0l5-5m-5 5h12"
            />
          </svg>
        </span>
        <span class="text-center text-xs"
          >Page {{ pagination.current_page }} of
          {{ pagination.total_pages }}</span
        >
        <span
          :disabled="!pagination.nextpage"
          :class="[
            !pagination.nextpage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="getPosts(pagination.nextpage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { shared } from '../../../../js/mixins/shared.js'
import { mapActions, mapGetters } from 'vuex'
import { EventBus } from '../../../../js/plugins/event-bus.js'
import PostsCard from '../../../../shared/components/PostsCard.vue'
import * as loadingAnimation from '../../../../shared/components/loading.json'
import { isDarkMode } from '../../../../js/store/careerwise/getters'

export default {
  components: {
    PostsCard,
  },

  mixins: [shared],

  data() {
    return {
      component_id: false,

      showPostDeleteModal: false,
      showPostDeleteModalContents: false,
      thinkingDelete: false,

      loaded: false,
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      posts: [],
      fromPostCount: 0,
      filter_posts_options: { draft: 1 },

      window: { width: 0, height: 0 },
      inputClass:
        'flex justify-center items-center sm:text-sm text-black focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out rounded bg-white px-4 h-12',

      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 9,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },

      type: 'Posts',
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      user: 'shared/getUser',
      currentPostsTemplateId: 'shared/getCurrentPostTemplateId',
      filterPostsBy: 'shared/getFilterPostsBy',
      customisation: 'careerwise/getCustomisation',
      isDarkMode: 'careerwise/isDarkMode',
    }),
  },

  watch: {
    showPostDeleteModal: {
      handler: function (active) {
        EventBus.$emit('modal_state_changing', active)
        if (!active) {
          this.$set(this, 'showPostDeleteModalContents', false)
        } else {
          setTimeout(() => {
            this.$set(this, 'showPostDeleteModalContents', true)
          }, 100)
        }
        this.$set(this, 'thinkingDelete', false)
      },

      deep: true,
    },

    posts: {
      handler: function () {
        this.setTags()
      },

      deep: true,
    },
  },

  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },

  mounted() {
    this.dispatchSetFilterPostsBy({ draft_reset: true })

    this.$set(this, 'component_id', this.makeId())

    this.dispatchSetCurrentPostsTemplateId(this.component_id)

    this.getPosts()

    EventBus.$on('showPostDeleteModal', (post_parent_id) => {
      this.$set(this, 'showPostDeleteModal', post_parent_id)
    })

    // Handles eventbus output from PostManagerMenu when publishing a post. Bad name due to missing this component in first version.
    EventBus.$on('getPostsWithFilterOptions', (data) => {
      if (data.parent === this.component_id || data === this.component_id) {
        this.getPosts(null, false)
      }
    })

    // Handles eventbus output from PostManagerMenu when deleting a post. Bad name due to missing this component in first version.
    EventBus.$on('getUpdatedPostsFromFilterComponent', () => {
      this.getPosts(null, false)
    })
  },

  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    ...mapActions({
      dispatchSetCurrentPostsTemplateId:
        'shared/dispatchSetCurrentPostsTemplateId',
      dispatchSetFilterPostsBy: 'shared/dispatchSetFilterPostsBy',
    }),

    confirmDeletePost(post_parent_id) {
      this.$set(this, 'thinkingDelete', true)
      EventBus.$emit('confirmDeletePost', post_parent_id)
    },

    newPost() {
      EventBus.$emit('showModal', 'new-post')
    },

    handleResize() {
      this.window.width = window.innerWidth
      this.window.height = window.innerHeight
    },

    setTags() {
      if (this.posts.length > 0) {
        this.posts.forEach((post) => {
          //general tags
          let general_arr = []
          this.settings.tags.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              general_arr.push(i.text)
            }
          })
          post.general_tags = general_arr

          // Year tags
          let year_arr = []
          this.settings.tag_years.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              year_arr.push(i.text)
            }
          })
          post.year_tag = year_arr

          // Region tags
          let region_arr = []
          this.settings.tag_regions.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              region_arr.push(i.text)
            }
          })
          post.region_tag = region_arr
        })
      }
    },

    /**
        ============================ GET POSTS WITH FILTER OPTION ===============
      */
    getPosts(gotopage = null, scroll = true) {
      /** Strange problem with vie router running an api call multiple times on mounted when navigating in and out of the same template.
       * We sovle this by creating a new unique component id for the template on mount and only honour the api request IF that component id is stores in $store.
       * do not want to run legacy componentns or routing api calls */
      if (this.component_id !== this.currentPostsTemplateId) {
        return
      }

      // If already loaded, assume we are searching or paginating, scroll to posts
      let scrollToPosts = false
      if (this.loaded && scroll) {
        scrollToPosts = true
      }

      let submitData = {}
      submitData.search = this.filter_posts_options
      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      if (this.showOldEvents) {
        submitData.oldEvents = true
      }

      // Get this post...
      axios.post('/api/posts', submitData).then((response) => {
        let result = response.data
        if (result.success) {
          this.$set(this, 'posts', result.posts)
          this.$set(this, 'loaded', true)

          // pagination.total = total # of posts. / by per_page, + 1. Pagination doesnt show when not needed so +1 is never wrong on single page
          let total_pages =
            Math.floor(
              response.data.pagination.total /
                response.data.pagination.per_page,
            ) + 1
          this.$set(
            this.pagination,
            'current_page',
            response.data.pagination.current_page,
          )
          this.$set(this.pagination, 'total_pages', total_pages)
          this.$set(
            this.pagination,
            'per_page',
            response.data.pagination.per_page,
          )
          this.$set(
            this.pagination,
            'last_page_url',
            response.data.pagination.last_page_url,
          )
          this.$set(
            this.pagination,
            'next_page_url',
            response.data.pagination.next_page_url,
          )
          this.$set(
            this.pagination,
            'prev_page_url',
            response.data.pagination.prev_page_url,
          )
          this.$set(
            this.pagination,
            'path',
            response.data.pagination.next_page_path,
          )

          let prev = null
          if (this.pagination.prev_page_url) {
            prev = this.pagination.prev_page_url.split('=')
            prev = prev[1]
          }

          let next = null
          if (this.pagination.next_page_url) {
            next = this.pagination.next_page_url.split('=')
            next = next[1]
          }

          this.$set(this.pagination, 'previouspage', prev)
          this.$set(this.pagination, 'nextpage', next)

          // Scroll to posts on pagination and search click
          if (scrollToPosts) {
            this.$scrollTo('#posts')
          }
        }

        EventBus.$emit('filter_posts_search_response_completed')
      })
    },
  },
}
</script>

<style scoped>
.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}

@media screen and (max-width: 1024px) {
  .post-intro::after {
    display: none;
  }
}
</style>
